// ---------------------------------------------------------------------------
// SSO session guard (framework-agnostic).
//
// Import once per app and call startSessionGuard() after boot. It keeps an
// ACTIVE session alive by silently refreshing the short-lived access token and
// ends an IDLE session promptly. It is driven entirely by the shared `spm_token`
// cookie + the central `/api/auth/sso/refresh` endpoint, so it behaves the same
// in every portal regardless of axios/fetch/CRA/Vite.
//
// Cross-subdomain idle detection uses the token's `iat` (issued-at) as a shared
// activity clock: any active tab/portal refreshes the shared cookie, keeping it
// "young", so an idle background tab will not log out a colleague who is active
// in another app. Only when NO tab has refreshed for `idleMs` does the session
// end.
// ---------------------------------------------------------------------------

export interface SessionGuardOptions {
  /**
   * Origin that issues/refreshes the shared session, e.g. https://apps.surbhi.net.
   * Leave empty (or pass the app's own origin) for the central app, whose refresh
   * endpoint is same-origin.
   */
  centralOrigin?: string;
  /** Hard idle timeout (ms). Session ends after this much inactivity. Default 30 min. */
  idleMs?: number;
  /** How often (ms) an active tab refreshes. Must be < idleMs and < access-token TTL. Default 5 min. */
  heartbeatMs?: number;
  /** Shared access cookie name. Default 'spm_token'. */
  cookieName?: string;
  /** Called when the session has ended. Default: redirect to the central login. */
  onExpired?: () => void;
}

let started = false;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const row = document.cookie.split('; ').find((r) => r.startsWith(name + '='));
  return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : null;
}

function jwtClaims(token: string): { exp?: number; iat?: number } {
  try {
    return JSON.parse(atob(token.split('.')[1] || '')) || {};
  } catch {
    return {};
  }
}

export function startSessionGuard(options: SessionGuardOptions = {}): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  const idleMs = options.idleMs ?? 30 * 60 * 1000;
  const heartbeatMs = options.heartbeatMs ?? 5 * 60 * 1000;
  const cookieName =
    options.cookieName ?? `spm_token${import.meta.env.VITE_AUTH_COOKIE_SUFFIX || ''}`;
  const central = (options.centralOrigin ?? '').replace(/\/+$/, '');
  const url = (path: string) => (central ? `${central}${path}` : path);

  const onExpired =
    options.onExpired ??
    (() => {
      const next = encodeURIComponent(window.location.href);
      window.location.replace(url(`/api/auth/sso/start?next=${next}`));
    });

  let lastActivity = Date.now();
  let lastRefresh = 0;
  let refreshing = false;
  let hadSession = !!readCookie(cookieName);

  const markActive = () => {
    lastActivity = Date.now();
  };
  ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'].forEach((ev) =>
    window.addEventListener(ev, markActive, { passive: true }),
  );

  async function refresh(): Promise<boolean> {
    if (refreshing) return true;
    refreshing = true;
    try {
      const res = await fetch(url('/api/auth/sso/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => null);
      // Keep localStorage in sync for apps that send a Bearer header from it.
      if (data && data.token) {
        try {
          localStorage.setItem('token', data.token);
          if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        } catch {
          /* ignore storage errors */
        }
      }
      return true;
    } catch {
      return false;
    } finally {
      refreshing = false;
      lastRefresh = Date.now();
    }
  }

  async function endSession(): Promise<void> {
    try {
      await fetch(url('/api/auth/sso/logout'), { method: 'POST', credentials: 'include' });
    } catch {
      /* best effort — cookies also lapse on their own */
    }
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch {
      /* ignore */
    }
    onExpired();
  }

  async function tick(): Promise<void> {
    const token = readCookie(cookieName);
    const idleLocal = Date.now() - lastActivity;

    if (!token) {
      // Stay passive if we never had a session (e.g. on the login screen) so we
      // never fight the app's own login redirect. If a session existed and then
      // vanished, it ended → hand off to onExpired.
      if (hadSession) onExpired();
      return;
    }
    hadSession = true;

    const { iat } = jwtClaims(token);
    const nowSec = Math.floor(Date.now() / 1000);
    const ageMs = iat ? (nowSec - iat) * 1000 : 0;

    // End the session only when THIS tab is idle AND the shared token has not been
    // refreshed by any tab/portal for idleMs (its iat has aged past the timeout).
    if (ageMs >= idleMs && idleLocal >= idleMs) {
      await endSession();
      return;
    }

    // While this tab is active, keep the shared session young (throttled).
    if (idleLocal < idleMs && Date.now() - lastRefresh >= heartbeatMs) {
      await refresh();
    }
  }

  const intervalMs = Math.min(Math.max(heartbeatMs / 5, 30 * 1000), 60 * 1000);
  window.setInterval(() => {
    void tick();
  }, intervalMs);
  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) void tick();
  });
  // Kick once shortly after boot so an already-expired session is caught quickly.
  window.setTimeout(() => void tick(), 5 * 1000);
}
